// src/modules/obras/services/exportFinnegans.js
//
// Genera los archivos de importación de pedidos de venta de Finnegans a partir
// del pedido OP de la obra:
//   · Pedido R8: perfiles/refuerzos por código + "Accesorios Varios" (ACCVARIOS)
//     y "Vidrios Float PVC" (981000100) con las cantidades totales del OP.
//   · Pedido tela (GUALE): tejido fibra de vidrio en rollos (Ceci elige producto).
// Formato: mismas columnas que la plantilla oficial ("archivo (41).xls").

import * as XLSX from "xlsx";

export const COD_ACCESORIOS = "ACCVARIOS";
export const COD_VIDRIOS = "981000100";

// Encabezados exactos de la plantilla de importación de Finnegans
export const COLUMNAS_PLANTILLA = [
  "NUMERO", "FECHA", "CLIENTE", "COMPROBANTE", "CONDICIONPAGO", "VENDEDOR",
  "SUCURSAL", "DESCRIPCION", "PRODUCTO", "DESCRIPCIONITEM", "CANTIDAD", "PRECIO",
  "WORKFLOW", "TIPOITEM", "FECHADESDE", "FECHAHASTA", "FECHAFACTURACION",
  "PERIODICIDAD", "RENOVACION", "DEPOSITOORIGEN", "DEPOSITODESTINO", "DIMENSION",
  "DIMENSIONVALOR", "DIMENSION2", "DIMENSIONVALOR2", "DIMENSION3", "DIMENSIONVALOR3",
  "FECHAENTREGA", "DEBITOAUTOMATICOOPBANC", "DEBITOAUTOMATICONROTAR", "PRECIOSOBRE",
  "PROVINCIA_ORIGEN_TRANS", "PROVINCIA_DESTINO_TRAN", "PROVINCIA_DESTINO_PROD",
  "EQUIPO_SOLICITANTE", "INTERMEDIARIO",
];

/**
 * Clasificación de productos Finnegans por depósito de origen (según prefijos
 * reales del maestro): 80x = perfiles/refuerzos PVC (R8); 66x = telas/pasadores,
 * 85x = accesorios Karpen, ACCVARIOS y 981000100 = Vidrios Float → GUALE.
 */
export function esProductoR8(cod) {
  const c = String(cod || "").trim();
  return /^80/.test(c);
}
export function esProductoGuale(cod) {
  const c = String(cod || "").trim();
  return /^(66|85)/.test(c) || c === COD_ACCESORIOS || c === COD_VIDRIOS;
}

/**
 * Valida que un pedido de Finnegans corresponda al destino elegido al vincular.
 * Devuelve un mensaje de advertencia o null si es consistente.
 */
export function validarDestinoPedido(productos, destino) {
  const cods = (productos || []).map(p => String(p.cod || "").trim()).filter(Boolean);
  if (cods.length === 0) return null;
  const r8 = cods.filter(esProductoR8).length;
  const guale = cods.filter(esProductoGuale).length;

  // Solo avisar si el pedido es CLARAMENTE del otro destino (mayoría de productos
  // del depósito contrario) — no por uno o dos productos sueltos mal clasificados.
  if (destino === "r8" && guale > r8) {
    return "Este pedido tiene sobre todo accesorios/telas — por sus productos parece el pedido de GUALEGUAY. ¿Vincularlo igual como pedido R8?";
  }
  if (destino === "guale" && r8 > guale) {
    return "Este pedido tiene sobre todo perfiles/refuerzos/vidrios — por sus productos parece el pedido de R8. ¿Vincularlo igual como pedido de Gualeguay?";
  }
  return null;
}

function fechaHoy() {
  // Celda de FECHA como fecha real de Excel (no texto): el adaptador de
  // Finnegans la rechaza si no es una celda de tipo fecha.
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function escapeRx(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Convención Finnegans: el accesorio lleva la inicial del color al final del
// código (KADC + NEGRO → KADCN). Se detecta el color en el detalle del OP.
const COLORES = [
  { palabra: "NEGRO", letra: "N" },
  { palabra: "BLANCO", letra: "B" },
  { palabra: "GRIS", letra: "G" },
  { palabra: "MADERA", letra: "M" },
  { palabra: "ANTRACITA", letra: "A" },
];

function letraColorDe(detalle) {
  const d = String(detalle || "").toUpperCase();
  const hit = COLORES.find(c => d.includes(c.palabra));
  return hit ? hit.letra : null;
}

/**
 * Resuelve el código Finnegans de cada ítem de la sección de perfiles del OP:
 *   1. código numérico que trae el detalle del OP
 *   2. memoria de elecciones anteriores (obras_alias)
 *   3. único producto activo del maestro cuyo nombre contiene el alias
 *   4. varios candidatos → lo elige el usuario en la previsualización
 * Devuelve [{ ...item, codigo, producto, origen, candidatos }].
 */
export function resolverItemsFinnegans(items, productos, aliasGuardados) {
  const porCodigo = new Map(productos.map(p => [String(p.codigo), p]));

  return items.map(it => {
    if (it.codNum) {
      return {
        ...it,
        codigo: it.codNum,
        producto: porCodigo.get(it.codNum) || null,
        origen: "codigo",
        candidatos: [],
      };
    }

    const guardado = aliasGuardados.get(it.alias);
    if (guardado && porCodigo.has(String(guardado))) {
      return {
        ...it,
        codigo: String(guardado),
        producto: porCodigo.get(String(guardado)),
        origen: "memoria",
        candidatos: [],
      };
    }

    // 3) alias exacto en nombre o código del maestro
    const rx = new RegExp(`\\b${escapeRx(it.alias)}\\b`, "i");
    const hits = productos.filter(p => rx.test(p.nombre) || rx.test(String(p.codigo)));
    const activos = hits.filter(p => p.activo);
    const pool = activos.length > 0 ? activos : hits;

    if (pool.length === 1) {
      return { ...it, codigo: String(pool[0].codigo), producto: pool[0], origen: "alias", candidatos: [] };
    }
    if (pool.length > 1) {
      return { ...it, codigo: null, producto: null, origen: null, candidatos: pool };
    }

    // 4) alias + sufijo de color (KADC → KADCN/KADCB…)
    const rxSufijo = new RegExp(`\\b${escapeRx(it.alias)}[A-Z]{1,2}\\b`, "i");
    const hitsSuf = productos.filter(p => rxSufijo.test(p.nombre) || rxSufijo.test(String(p.codigo)));
    const activosSuf = hitsSuf.filter(p => p.activo);
    const poolSuf = activosSuf.length > 0 ? activosSuf : hitsSuf;

    const letra = letraColorDe(it.detalle);
    if (letra) {
      const rxColor = new RegExp(`\\b${escapeRx(it.alias)}${letra}\\b`, "i");
      const conColor = poolSuf.filter(p => rxColor.test(p.nombre) || rxColor.test(String(p.codigo)));
      if (conColor.length === 1) {
        return { ...it, codigo: String(conColor[0].codigo), producto: conColor[0], origen: "color", candidatos: [] };
      }
    }
    if (poolSuf.length === 1) {
      return { ...it, codigo: String(poolSuf[0].codigo), producto: poolSuf[0], origen: "sufijo", candidatos: [] };
    }
    return { ...it, codigo: null, producto: null, origen: null, candidatos: poolSuf };
  });
}

/** Renglón agrupador (Accesorios Varios / Vidrios): cantidad y precio promedio del OP */
export function armarGrupo(items, codigo, nombre) {
  if (!items.length) return null;
  const cantidad = items.reduce((s, it) => s + it.cant, 0);
  const importe = items.reduce((s, it) => s + (it.total || 0), 0);
  return {
    codigo,
    nombre,
    cantidad,
    precio: cantidad > 0 ? Math.round((importe / cantidad) * 100) / 100 : 0,
    importe: Math.round(importe * 100) / 100,
  };
}

/**
 * Descarga un .xls de importación de pedido de Finnegans.
 * renglones: [{ codigo, cantidad, precio, descripcionItem }]
 */
export function descargarExcelPedido(nombreArchivo, renglones, { clienteCodigo, descripcion }) {
  const fecha = fechaHoy();
  const data = renglones.map(r => ({
    NUMERO: 1,
    FECHA: fecha,
    CLIENTE: clienteCodigo,
    COMPROBANTE: "",
    // Contado Efectivo — si el cliente no tiene condición predeterminada,
    // Finnegans rechaza el pedido sin este campo
    CONDICIONPAGO: "CE",
    // Circuito de comprobantes del pedido de venta — mismo código que usa
    // la integración por API (functions/finnegans/orders.js)
    WORKFLOW: "VENTAS",
    // Centro de costo (100% a CCO5) — sin esto el import rechaza los ítems
    DIMENSION: "DIMCTC",
    DIMENSIONVALOR: "CCO5",
    DESCRIPCION: descripcion,
    PRODUCTO: r.codigo,
    DESCRIPCIONITEM: (r.descripcionItem || "").slice(0, 120),
    CANTIDAD: r.cantidad,
    PRECIO: r.precio ?? "",
  }));

  const ws = XLSX.utils.json_to_sheet(data, { header: COLUMNAS_PLANTILLA, cellDates: true });

  // Formato dd/mm/yy en la columna FECHA (columna B, filas de datos)
  const colFecha = COLUMNAS_PLANTILLA.indexOf("FECHA");
  for (let r = 1; r <= data.length; r++) {
    const celda = ws[XLSX.utils.encode_cell({ r, c: colFecha })];
    if (celda) { celda.t = "d"; celda.z = "dd/mm/yy"; }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet0");
  XLSX.writeFile(wb, nombreArchivo, { bookType: "xls", cellDates: true });
}
